import React, { useState } from 'react';
import { Button, message, Modal, Upload } from 'antd';
import { CameraOutlined, UploadOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { slideAction } from '../../store/slideSlice';
import { templateService } from '../../store/api';
import { getSnapshot } from '../../utils/utils';
import defaultTemplateImage from '../../assets/template-default-image.png';
import './TemplateThumbnail.css';

const TemplateThumbnail = ({ viewMode, onMenuClick }) => {

  const dispatch = useDispatch();
  const { id } = useParams();
  const isTemplateDocument = useSelector((state) => state.slide.isTemplateDocument);
  const templateData = useSelector((state) => state.slide.templateData);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [customUrl, setCustomUrl] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const existingThumbnail = templateData?.thumbnail || defaultTemplateImage;

  const handleOpenModal = () => {
    setPreviewUrl(null);
    setIsModalVisible(true);
  };

  const handleCapture = async () => {
    try {
      // Check if slide element exists
      const slideElement = document.getElementById('slide01');
      if (!slideElement) {
        message.error({ content: 'No slide available to capture. Please open a slide first.', key: 'capture' });
        return;
      }

      setIsCapturing(true);
      message.loading({ content: 'Capturing slide...', key: 'capture' });
      const dataUrl = await getSnapshot();
      setPreviewUrl(dataUrl);
      message.success({ content: 'Slide captured successfully!', key: 'capture' });
    } catch (error) {
      console.error('Failed to capture slide:', error);
      message.error({ content: 'Failed to capture slide', key: 'capture' });
    } finally {
      setIsCapturing(false);
    }
  };

  const handleConfirmUpdate = async () => {
    try {
      const thumbnailUrl = customUrl || previewUrl;

      if (!thumbnailUrl) {
        message.warning('Please capture or upload a thumbnail first');
        return;
      }

      const res = await templateService.updateTemplate({ templateId: id, templateThumbnail: thumbnailUrl });
      message.success('Thumbnail saved successfully');
      setIsModalVisible(false);
      setPreviewUrl(null);
      setCustomUrl(null);
      // Update templateData in store
      if (res?.data) {
        dispatch(slideAction.setTemplateData({ ...templateData, ...res.data, thumbnail: thumbnailUrl }));
      } else {
        dispatch(slideAction.setTemplateData({ ...templateData, thumbnail: thumbnailUrl }));
      }
    } catch (error) {
      console.error('Failed to save thumbnail:', error);
      message.error('Failed to save thumbnail');
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setPreviewUrl(null);
    setCustomUrl(null);
  };

  const handleUpload = (info) => {
    const file = info.file;
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCustomUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const modalContent = (
    <Modal
      title={<div className="thumbnail-modal-header"><CameraOutlined className="thumbnail-modal-icon" />Thumbnail Preview</div>}
      visible={isModalVisible}
      onCancel={handleCancel}
      className="thumbnail-modal"
      width={560}
      footer={[
        <Button key="cancel" onClick={handleCancel} className="cancel-btn">
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleConfirmUpdate} className="update-thumbnail-btn">
          Save Thumbnail
        </Button>,
      ]}
    >
      <div className="thumbnail-content">
        <div className="thumbnail-preview-container" role="img" aria-label="thumbnail-preview">
          {(customUrl || previewUrl || existingThumbnail) ? (
            <img src={customUrl || previewUrl || existingThumbnail} alt="Thumbnail Preview" className="thumbnail-preview" />
          ) : (
            <div className="thumbnail-placeholder">
              <div className="placeholder-icon">📄</div>
              <p>No thumbnail yet</p>
            </div>
          )}
        </div>
        <div className="upload-section">
          <div className="upload-label">Upload a custom thumbnail</div>
          <Upload
            beforeUpload={() => false}
            onChange={handleUpload}
            showUploadList={false}
            accept="image/*"
          >
            <Button icon={<UploadOutlined />} className="upload-btn">Upload Custom Thumbnail</Button>
          </Upload>
          <div className="capture-label">Capture current view</div>
          <Button 
            icon={<CameraOutlined />} 
            onClick={handleCapture} 
            className="capture-btn"
            loading={isCapturing}
            disabled={isCapturing}
          >
            {isCapturing ? 'Capturing...' : 'Capture Current View'}
          </Button>
        </div>
      </div>
    </Modal>
  );

  if (!isTemplateDocument) return null;

  if (viewMode === "menu") {
    return (
      <>
        <div className='menu-item' onClick={() => { handleOpenModal(); onMenuClick && onMenuClick(); }}>
          <CameraOutlined className="toggle-menu-icon" />
          <span>Update Thumbnail</span>
        </div>
        {modalContent}
      </>
    );
  }

  return (
    <>
      <Button
        type="text"
        icon={<CameraOutlined className="thumbnail-button-icon" />}
        onClick={handleOpenModal}
        title="Save Thumbnail"
        size="small"
        className="thumbnail-button"
      />
      {modalContent}
    </>
  );
};

export default TemplateThumbnail;